import React from 'react'
import { useHistory } from 'react-router'

import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { Call, SecurityUser, Sms, UserOctagon } from 'iconsax-react'

import AgentPage from '../../components/templates/AgentPage'
import Table from '../../components/molecules/Table'
import Loading from '../../components/molecules/Table/Loading'
import Header from '../../components/molecules/Table/Header'
import Client from '../../components/molecules/Table/Cells/Client'
import Channel from '../../components/molecules/Table/Cells/Channel'
import ActionButton from '../../components/organisms/ActionButton'
import ErrorToast from '../../components/molecules/ErrorToast'

import { filterPermissions, parseSort } from './utils'

import { Routes } from '../../Routes'

import { columnHelper } from './helpers'
import useDelayedUserSearch, {
  DEFAULT_FILTER,
  MIN_CHAR_COUNT
} from '@SonarCX/hooks/useDelayedUserSearch'
import useAccess from '@SonarCX/hooks/useAccess'
import { useUserAPI, DEFAULT_EMBEDS } from '@SonarCX/providers/UserAPIProvider'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { selectRole } from '@SonarCX/slices/role'
import {
  fetchPageOfUsers,
  resetPagination,
  selectUsersTable,
  setPaginationPage,
  setPaginationSize,
  setSort
} from '@SonarCX/slices/usersTable'
import useSavedValue from '@SonarCX/hooks/useSavedValue'
import useStyles from './styles'

export const USER_SEARCH_STORAGE_KEY = 'user_search'

const People = ({ external }) => {
  const history = useHistory()
  const dispatch = useAppDispatch()

  const { roles: apiRoles } = useAppSelector(selectRole)
  const { pagination, tableApiSort } = useAppSelector(selectUsersTable)
  const { updateUsers } = useUserAPI()
  const {
    search: handleSearch,
    results,
    error,
    loading: searchLoading
  } = useDelayedUserSearch(external, DEFAULT_FILTER, DEFAULT_EMBEDS)
  const hasPermission = useAccess()
  const { user: currentUser } = useEnvironment()
  const { isSmall, isMedium } = useBreakpoint()

  const showPagination = React.useMemo(() => {
    if (!pagination) {
      return false
    }

    const { per_page, total } = pagination
    return per_page < total
  }, [pagination])

  // Format fetched roles
  const roles = React.useMemo(() => {
    const roleArr = Object.values(apiRoles)
    if (roleArr.length === 0) {
      return []
    }

    const guest = { id: null, name: 'Guest' }
    const customer = { id: 'Customer', name: 'Customer' }
    const newRoles = [guest, customer, ...roleArr]
    return newRoles
  }, [apiRoles])

  const [searchFilter, setSearchFilter] = useSavedValue({
    key: USER_SEARCH_STORAGE_KEY,
    defaultValue: '""'
  })
  const canSearch = React.useMemo(
    () => searchFilter.length > MIN_CHAR_COUNT,
    [searchFilter]
  )
  const [applySearchFilter, setApplySearchFilter] = React.useState(false)
  const [loadingPages, setLoadingPages] = React.useState(false)
  const [initialLoad, setInitialLoad] = React.useState(false)
  const [tableSort, setTableSort] = React.useState([])
  const [serverSort, setServerSort] = React.useState([])
  const [data, setData] = React.useState([])

  const classes = useStyles()

  const loadTableData = React.useCallback(
    (pageSize = null, page = null, sort = undefined) => {
      setLoadingPages(true)
      dispatch(
        fetchPageOfUsers(
          external,
          pageSize ? pageSize : pagination.per_page,
          page !== null ? page : pagination.page,
          null,
          sort !== undefined ? sort : serverSort,
          data => {
            setData(data)
            updateUsers(data)
            setLoadingPages(false)
          }
        )
      )
    },
    [
      dispatch,
      updateUsers,
      pagination.page,
      pagination.per_page,
      serverSort,
      external
    ]
  )

  const reloadTableData = React.useCallback(() => {
    dispatch(
      fetchPageOfUsers(
        external,
        pagination.per_page,
        pagination.page,
        null,
        serverSort,
        data => {
          setData(data)
          updateUsers(data)
        }
      )
    )
  }, [
    dispatch,
    updateUsers,
    pagination.page,
    pagination.per_page,
    serverSort,
    external
  ])

  const reloadAfterNew = React.useCallback(() => {
    reloadTableData()
  }, [reloadTableData])

  // Does filtering and sorting of users
  const preparedUsers = React.useMemo(() => {
    const filtered = applySearchFilter ? results : data

    const permFiltered = filterPermissions(
      hasPermission,
      filtered,
      true,
      currentUser
    )

    return [...permFiltered]
  }, [applySearchFilter, hasPermission, currentUser, data, results])

  // Handles search results
  React.useEffect(() => {
    if (canSearch) {
      updateUsers(results)
      setApplySearchFilter(true)
      setLoadingPages(false)
    }
  }, [updateUsers, results, canSearch])

  // Trigger debounce search
  React.useEffect(() => {
    if (canSearch) {
      setLoadingPages(canSearch)
      handleSearch(searchFilter, null, null, serverSort)
    } else {
      setApplySearchFilter(false)
    }
  }, [canSearch, handleSearch, searchFilter, serverSort])

  const handleRowClicked = React.useCallback(
    row => {
      history.push(`${Routes.USERS}/${row.original.id}`)
    },
    [history]
  )

  // Contact page specific columns
  const columns = React.useMemo(() => {
    const avatarColumn = columnHelper.accessor('id', {
      header: () => <Header>Users</Header>,
      cell: ({ row }) => (
        <Client
          client={row.original}
          onClick={() => {
            handleRowClicked(row)
          }}
        />
      )
    })

    const accountColumn = isSmall
      ? null
      : columnHelper.accessor('auth', {
          header: () => <Header Icon={UserOctagon}>Account</Header>,
          enableSorting: false,
          cell: ({ row }) => {
            const channels = []
            if (row.original.auth) {
              channels.push({
                type: 'email',
                value: row.original.auth.email
              })
            }
            const user = { ...row.original, channels }
            return <Channel user={user} isEmail hideAlert />
          }
        })

    const emailColumn = isMedium
      ? null
      : columnHelper.accessor('email', {
          header: () => <Header Icon={Sms}>Email</Header>,
          enableSorting: false,
          cell: ({ row }) => <Channel user={row.original} isEmail />
        })

    const phoneColumn = isMedium
      ? null
      : columnHelper.accessor('phone', {
          header: () => <Header Icon={Call}>Phone</Header>,
          enableSorting: false,
          cell: ({ row }) => <Channel user={row.original} />
        })

    const roleColumn = columnHelper.accessor('role_id', {
      header: () => <Header Icon={SecurityUser}>Role</Header>,
      cell: ({ row }) => {
        if (row.original.role_id) {
          return (
            roles.find(role => role.id === row.original.role_id)?.name || ''
          )
        }

        return row.original.auth ? 'Customer' : 'Guest'
      }
    })

    return [
      avatarColumn,
      accountColumn,
      emailColumn,
      phoneColumn,
      roleColumn
    ].filter(Boolean)
  }, [isSmall, isMedium, handleRowClicked, roles])

  // Resets pagination state and fetches first page
  const updatePageSize = React.useCallback(
    async size => {
      dispatch(resetPagination())
      dispatch(setPaginationSize(size))
      //loadTableData(size, 0)
    },
    [dispatch]
  )

  // Goes to another page, fetching if required
  const changePage = React.useCallback(
    async newPage => {
      dispatch(setPaginationPage(newPage))
      loadTableData(null, newPage)
    },
    [dispatch, loadTableData]
  )

  React.useEffect(() => {
    if (!initialLoad) {
      const newSort = tableApiSort.map(item => parseSort(item))
      const filtered = newSort.filter(Boolean)
      loadTableData(null, null, filtered)
      setInitialLoad(old => !old)
    }
  }, [initialLoad, loadTableData, currentUser, tableApiSort])

  React.useEffect(() => {
    if (tableSort.length >= 1) {
      const newSort = tableSort.map(item => parseSort(item))
      const filtered = newSort.filter(Boolean)
      setServerSort(filtered)
      dispatch(setSort({ sort: filtered, apiTableSort: tableSort }))
      loadTableData(null, null, filtered)
    }
    //eslint-disable-next-line
  }, [tableSort])

  const initialSort = React.useMemo(() => [...tableApiSort], [tableApiSort])

  return (
    <>
      <AgentPage scroll>
        {!initialLoad ? (
          <Loading />
        ) : (
          <div className={classes.root}>
            <Table
              data={preparedUsers}
              columns={columns}
              filter={applySearchFilter ? '' : searchFilter}
              onRowClicked={handleRowClicked}
              message={searchLoading ? 'Searching users...' : 'There are no users'}
              loaded={!loadingPages}
              enableSort
              // Pagination
              onSetPageSize={updatePageSize}
              onPageChange={changePage}
              showPagination={showPagination}
              pageNum={pagination.page}
              numResults={canSearch ? results.length : pagination.total}
              manualPagination={!canSearch}
              pagesize={pagination.per_page}
              useManualSort={true}
              setTableSort={setTableSort}
              disableSortRemove={true}
              initialSort={initialSort}
              // UI
              isSmall={isSmall}
              padBottom={isSmall}
              disableHeader={isSmall}
            >
              <Table.Header>
                <Table.TopRow
                  title={
                    <div className={classes.title}>
                      Users
                      {isSmall && pagination?.total && (
                        <small>
                          ({canSearch ? results.length : pagination.total}{' '}
                          results)
                        </small>
                      )}
                    </div>
                  }
                />

                <Table.BottomRow
                  searchVal={searchFilter}
                  onSearch={filter =>
                    setSearchFilter(filter.trim().toLocaleLowerCase())
                  }
                  fullWidth={true}
                  isSmall={isSmall}
                  className={classes.bottomRow}
                />
              </Table.Header>
            </Table>
          </div>
        )}
      </AgentPage>

      {!isSmall && (
        <ActionButton
          screen='user'
          cb={reloadAfterNew}
          canShowUser={!external}
        />
      )}

      <ErrorToast error={error} />
    </>
  )
}

export default People
